/**
 * Pure, framework-free "Beat My Score" domain logic - types, comparison,
 * escaping, and formatting shared by the Netlify Functions, the Edge
 * Function, and every Scene that touches a score_challenges row. No
 * network, no Phaser, no `window` - see `src/daily/__tests__/scoreChallenge.test.ts`.
 */

/** A v4 UUID - the ONLY shape a public challenge id may ever have (see `score_challenges.public_id`). Deliberately requires the version/variant nibbles a real `gen_random_uuid()` always sets, so a hand-typed guess can't even pass this shape check. */
export const SCORE_CHALLENGE_PUBLIC_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isValidScoreChallengePublicId(value: unknown): value is string {
  return typeof value === "string" && SCORE_CHALLENGE_PUBLIC_ID_RE.test(value);
}

export type ScoreChallengeStatus = "active" | "expired" | "invalid";

/**
 * PR26 follow-up: mirrors `get_score_challenge()`'s exact `'active'` vs
 * `'expired'` resolution (see `013_daily_independent_version_bests.sql`) -
 * same precedent as `dailyRanking.ts`/`leaderboardOrdering.ts`: no Postgres
 * test harness exists in this repo, so this pure, network-independent
 * function is the single unit-tested definition the SQL comments point
 * back to. Takes the row as it already exists (an unknown/never-created
 * `public_id` is `'invalid'`, resolved by the RPC/caller before this ever
 * runs - a pure existence check with nothing here to mirror).
 *
 * `'expired'` now covers TWO structurally different but user-facing-
 * identical cases, both rendered with the exact same "THIS CHALLENGE HAS
 * ENDED / A new Daily Challenge is live." copy (`ScoreChallengeScene`):
 *  - the classic case - `row.challengeId` is no longer today's date; and
 *  - PR26 follow-up - `row.challengeId` IS still today's date, but the
 *    challenge's PINNED `scoringRulesVersion` (fixed at creation, see
 *    `score_challenges.scoring_rules_version`) no longer matches the
 *    CALLING client's current `callerRulesVersion` - e.g. a link created
 *    and shared under scoring-rules v1, opened by a v2 client after a
 *    mid-day scoring-constants deploy. The row itself is never deleted or
 *    rewritten either way - only this read-path stops presenting it as an
 *    active, fairly-comparable target.
 */
export function resolveScoreChallengeStatus(
  row: { challengeId: string; scoringRulesVersion: number },
  todayChallengeId: string,
  callerRulesVersion: number,
): "active" | "expired" {
  if (row.challengeId !== todayChallengeId) return "expired";
  if (row.scoringRulesVersion !== callerRulesVersion) return "expired";
  return "active";
}

/** What `get_score_challenge()` returns, camelCased - see `supabase/migrations/006_score_challenge.sql`. Every field but `status` is `null` unless `status === "active"`. */
export interface ScoreChallengeView {
  status: ScoreChallengeStatus;
  challengeId: string | null;
  targetDailyPoints: number | null;
  creatorDisplayName: string | null;
  analyticsId: string | null;
  isCreator: boolean;
  expiresAtMs: number | null;
}

/** Carried from a challenge landing page, through `DailyChallengeScene`/`GameScene`, into `DailyResultScene` - never re-fetched mid-run, and never influences scoring/attempts (display + comparison only). */
export interface SourceChallengeContext {
  publicId: string;
  targetDailyPoints: number;
  creatorDisplayName: string;
  analyticsId: string;
}

export type ScoreComparisonOutcome = "beat" | "tie" | "lost";

export interface ScoreComparisonResult {
  outcome: ScoreComparisonOutcome;
  /** Always >= 0 - the caller decides which side "led" from `outcome`. */
  margin: number;
}

/** Only a STRICTLY higher score beats the target - an equal score is always a tie, per the PR spec. */
export function compareToTarget(visitorDailyPoints: number, targetDailyPoints: number): ScoreComparisonResult {
  if (visitorDailyPoints > targetDailyPoints) return { outcome: "beat", margin: visitorDailyPoints - targetDailyPoints };
  if (visitorDailyPoints < targetDailyPoints) return { outcome: "lost", margin: targetDailyPoints - visitorDailyPoints };
  return { outcome: "tie", margin: 0 };
}

/** Coarse, non-identifying bucket for a comparison margin - safe to send to PostHog (see `score_challenge_verified_result`'s properties); never the exact margin, which combined with the exact target could reconstruct both raw scores. */
export function marginBucket(margin: number): "0" | "1-10" | "11-50" | "51-200" | "201-500" | "500+" {
  if (margin <= 0) return "0";
  if (margin <= 10) return "1-10";
  if (margin <= 50) return "11-50";
  if (margin <= 200) return "51-200";
  if (margin <= 500) return "201-500";
  return "500+";
}

/** Coarse bucket for "attempts remaining" - always one of exactly 4 values, safe to send to PostHog. */
export function attemptsRemainingBucket(attemptsRemaining: number): "0" | "1" | "2" | "3" {
  const clamped = Math.max(0, Math.min(3, Math.trunc(attemptsRemaining)));
  return String(clamped) as "0" | "1" | "2" | "3";
}

/** Escapes the 5 HTML-significant characters - used both for the Open Graph/social-preview Edge Function (raw HTML string interpolation) and as defense-in-depth anywhere a display-name snapshot is rendered as text outside Phaser's own canvas-drawn Text objects (which never interpret HTML in the first place, but this module is shared by the Edge Function, which does emit raw HTML). */
export function escapeHtml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/** The exact headline/subline pair for a win/tie/loss comparison card - shared by `ScoreChallengeScene` (a visitor comparing against a creator's target) and `DailyResultScene` (a challenge-flow participant seeing their own just-verified result compared to the same target), so the two screens can never phrase the same outcome differently. `subline` is `""` for a tie (no margin to report). */
export function describeComparison(comparison: ScoreComparisonResult, opponentDisplayName: string): { headline: string; subline: string } {
  const name = truncateDisplayName(opponentDisplayName, 18).toUpperCase();
  if (comparison.outcome === "beat") return { headline: `YOU BEAT ${name}`, subline: `BY ${comparison.margin.toLocaleString("en-US")} DAILY POINTS` };
  if (comparison.outcome === "tie") return { headline: "IT'S A TIE", subline: "" };
  return { headline: `${name} LEADS`, subline: `BY ${comparison.margin.toLocaleString("en-US")} DAILY POINTS` };
}

/** Visually truncates an already-validated display name (2-32 chars, see `creator_display_name_snapshot`'s own bound) for a fixed-width UI slot - never re-validates or re-escapes; callers still escape separately where HTML injection is the concern (`escapeHtml`). */
export function truncateDisplayName(name: string, maxChars: number): string {
  if (name.length <= maxChars) return name;
  return `${name.slice(0, Math.max(1, maxChars - 1))}…`;
}

/** `"4H 12M"`-style countdown to a Daily reset/challenge expiry - deliberately coarser than `DailyChallengeScene`'s own `HH:MM:SS` countdown (that one drives a live per-second display; this one is a short, static landing-card label per the PR spec's "ENDS IN 4H 12M" example). Never negative - a already-expired challenge is a completely different UI state (`ScoreChallengeStatus: "expired"`), not a `0H 0M` label. */
export function formatEndsIn(remainingMs: number): string {
  const totalMinutes = Math.max(0, Math.round(remainingMs / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes}M`;
  return `${hours}H ${minutes}M`;
}
